// Cross-tab identity guard (issue #301).
//
// A login, tenant switch or logout in ANOTHER tab changes the shared persisted
// auth blob (localStorage 'vharta-auth'), but this tab keeps rendering — and,
// worse, writing — as the identity it loaded with. This module detects that.
//
// Triggers: BroadcastChannel('vharta-auth') where available, and the `storage`
// event on 'vharta-auth' as the fallback (and the logout signal: logout removes
// the key). Both are ADVISORY. A trigger carries no tokens and is never acted
// on directly; evaluate() re-reads the persisted blob (shared same-origin
// state: ids and tenant name only) and compares ids with this tab's own store,
// so a spurious or forged message that disagrees with storage does nothing,
// and token-only refreshes / identical rewrites (same ids) never fire.
// Deviation noted for review: the expert asked for re-verification with
// /auth/me; in bearer mode /auth/me answers for THIS tab's token, not the other
// tab's, so it cannot confirm the change. Everything this module triggers is
// non-destructive (stale flag, banner, local teardown, navigation); it never
// calls logout(), which would revoke the OTHER tab's session.

import { useAuthStore } from '@/stores/authStore'

export const AUTH_STORAGE_KEY = 'vharta-auth'
export const AUTH_CHANNEL = 'vharta-auth'

export type CrossTabChange =
  | { kind: 'tenant'; tenantName: string | null }
  | { kind: 'user' }
  | { kind: 'signed_out' }

interface PersistedIdentity {
  userId: string | null
  tenantId: string | null
  tenantName: string | null
}

function readPersistedIdentity(): PersistedIdentity | null {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY)
    if (!raw) return null
    const user = (JSON.parse(raw) as { state?: { user?: { id?: unknown; tenantId?: unknown; tenantName?: unknown } | null } })
      ?.state?.user
    if (!user || typeof user.id !== 'string') return null
    return {
      userId: user.id,
      tenantId: typeof user.tenantId === 'string' ? user.tenantId : '',
      tenantName: typeof user.tenantName === 'string' ? user.tenantName : null,
    }
  } catch {
    return null
  }
}

/** Compare the persisted (other-tab) identity with this tab's. Pure-ish, exported for tests. */
export function detectChange(): CrossTabChange | null {
  const mine = useAuthStore.getState().user
  if (!mine) return null // nothing to protect (login page, not yet signed in)
  const persisted = readPersistedIdentity()
  if (!persisted) return { kind: 'signed_out' }
  if (persisted.userId !== mine.id) return { kind: 'user' }
  if ((persisted.tenantId ?? '') !== (mine.tenantId ?? '')) return { kind: 'tenant', tenantName: persisted.tenantName }
  return null
}

/** Tell other tabs the identity here changed. Ids only — never a token. */
export function announceIdentity(): void {
  if (typeof BroadcastChannel === 'undefined') return
  try {
    const user = useAuthStore.getState().user
    const channel = new BroadcastChannel(AUTH_CHANNEL)
    channel.postMessage({ type: 'identity', userId: user?.id ?? null, tenantId: user?.tenantId ?? null })
    channel.close()
  } catch {
    // Advisory only; the storage event still fires.
  }
}

/**
 * Start listening. `onChange` fires at most once per distinct change. Also
 * announces this tab's own identity changes. Returns a stop function.
 */
export function startCrossTabSync(onChange: (change: CrossTabChange) => void): () => void {
  let lastKey = ''
  const evaluate = () => {
    const change = detectChange()
    if (!change) return
    const key = JSON.stringify(change)
    if (key === lastKey) return
    lastKey = key
    onChange(change)
  }

  const onStorage = (e: StorageEvent) => {
    // key === null means storage.clear() — treat like our key changed.
    if (e.key === AUTH_STORAGE_KEY || e.key === null) evaluate()
  }
  window.addEventListener('storage', onStorage)

  let channel: BroadcastChannel | null = null
  if (typeof BroadcastChannel !== 'undefined') {
    channel = new BroadcastChannel(AUTH_CHANNEL)
    channel.onmessage = () => evaluate() // advisory: content ignored, storage decides
  }

  let prev = (() => {
    const u = useAuthStore.getState().user
    return `${u?.id ?? ''}:${u?.tenantId ?? ''}`
  })()
  const unsubscribe = useAuthStore.subscribe((s) => {
    const next = `${s.user?.id ?? ''}:${s.user?.tenantId ?? ''}`
    if (next !== prev) {
      prev = next
      lastKey = ''
      announceIdentity()
    }
  })

  return () => {
    window.removeEventListener('storage', onStorage)
    channel?.close()
    unsubscribe()
  }
}
